import type { BaseItemSchema, Distribution } from "@memorang/types";
/* ----------------- React --------------- */
import { useCallback, useState } from "react";

/* ----------------- Hooks --------------- */
import useSessionStore from "./useSessionStore";

/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";

/* ----------------- Constants --------------- */
import { events, type SessionSource } from "@constants/tracking";

import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { IS_MOCKED_SESSION } from "@features/session/constants";
import { invalidateUserDataQueries } from "@helpers/query-invalidation";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useActiveStudyPackCollectionId } from "@hooks/useActiveStudyPackCollectionId";
import { useAppStore } from "@hooks/useAppStore";
import useCacheKey from "@hooks/useCacheKey";
import type { BackendSessionResponse, SessionResponse } from "@memorang/types";
import type { ExamStatus } from "@memorang/types";
import { useToast } from "@memorang/ui/components/Toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
/* ----------------- Router --------------- */
import { useRouter } from "expo-router";
import { createSession, endSession, fetchSessionItems } from "../api";
import type { CreateSessionRequest, ResumeSessionRequest } from "../api/types";
import { reconcileSummativeAssessments } from "../helpers/reconcile-summative-assessments";
import { flattenSessionItems } from "../helpers/session";

export const useSession = () => {
	const router = useRouter();

	const { showToast } = useToast();
	const { userId } = useAuthContext();

	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateProgress = useSessionStore((state) => state.updateProgress);
	const showItemsSlider = useAppStore(
		(state) => state.app.sessionConfig?.showItemsSlider,
	);

	const resumeSessionMutation = useMutation({
		mutationKey: [cacheKeys.resumeSession],
		mutationFn: fetchSessionItems,
	});

	const {
		currentExamContext: { organizationId, projectId, studyPackCollectionId },
	} = useExamContext();
	const {
		collectionId: activeStudyPackCollectionId,
		isLoading: isActiveStudyPackCollectionLoading,
	} = useActiveStudyPackCollectionId();
	const { baseCacheKey } = useCacheKey();

	const resumeSession = (request: ResumeSessionRequest) => {
		return resumeSessionMutation.mutateAsync(request);
	};
	const queryClient = useQueryClient();
	const updateEndingSessionInProgress = useSessionStore(
		(state) => state.updateEndingSessionInProgress,
	);

	const toggleShowErrorDialog = () => {
		setShowErrorDialog((prev) => !prev);
	};

	const createSessionMutation = useMutation({
		mutationKey: [cacheKeys.createSession],
		mutationFn: createSession,
	});

	const endSessionMutation = useMutation({
		mutationKey: [baseCacheKey, cacheKeys.endSession],
		mutationFn: endSession,
		onSuccess: () => {
			const { isSummativeTest, sessionSubType, sessionType, numericId } =
				useSessionStore.getState();
			trackCustomEvent({
				eventName: events.sessionEnded,
				sessionType:
					sessionType ?? (isSummativeTest ? "SUMMATIVE" : "FORMATIVE"),
				sessionSubType,
				numericId,
			});
			updateEndingSessionInProgress(false);
			invalidateUserDataQueries(queryClient);
		},
	});

	const createNewSession = useCallback(
		(request: CreateSessionRequest) => {
			return createSessionMutation.mutateAsync(request);
		},
		[createSessionMutation],
	);

	const endCurrentSession = useCallback(
		({ id }: { id: number }) => {
			updateEndingSessionInProgress(true);
			return endSessionMutation.mutateAsync({ sessionId: id });
		},
		[updateEndingSessionInProgress, endSessionMutation.mutateAsync],
	);

	const handlePostSessionCreation = ({
		newSessionResponse,
		numItemsRequested,
		dist,
		tags,
		handleClose,
		push,
		isDiagnostic,
		lastUpdatedItemIndex,
		isSummativeTest,
		isResumedSession,
		source,
	}: {
		newSessionResponse: SessionResponse;
		numItemsRequested: number;
		dist: Distribution;
		tags?: number[][];
		handleClose?: () => void;
		push?: boolean;
		lastUpdatedItemIndex?: number;
		isDiagnostic?: boolean;
		isSummativeTest?: boolean;
		isResumedSession?: boolean;
		source: SessionSource;
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	}) => {
		if (!isResumedSession) {
			trackCustomEvent({
				eventName: events.sessionStarted,
				numItems: numItemsRequested,
				dist: JSON.stringify(dist),
				tags: JSON.stringify(tags || []),
				sessionType:
					newSessionResponse.sessionType ??
					(isSummativeTest ? "SUMMATIVE" : "FORMATIVE"),
				sessionSubType: newSessionResponse.sessionSubType,
				source,
				numericId: newSessionResponse.numericId,
			});
		}
		if (lastUpdatedItemIndex && lastUpdatedItemIndex > 0) {
			const numItems = newSessionResponse.sessionItems.length;
			const progress = (lastUpdatedItemIndex + 1) / numItems;
			updateProgress(progress);
		}
		updateSession({
			sessionResponse: newSessionResponse,
			numItemsRequested,
			isSummativeTest,
			isDiagnostic,
			isResumedSession,
			hideMoreItemsAlert: !showItemsSlider,
		});
		if (handleClose || push) {
			handleClose?.();
			router.push({
				pathname: "/(protected)/session/[id]",
				params: {
					id: newSessionResponse.id,
					index:
						lastUpdatedItemIndex && lastUpdatedItemIndex > 0
							? lastUpdatedItemIndex + 1
							: 1,
				},
			});
		} else {
			router.replace({
				pathname: "/(protected)/session/[id]",
				params: {
					id: newSessionResponse.id,
					index:
						lastUpdatedItemIndex && lastUpdatedItemIndex > 0
							? lastUpdatedItemIndex + 1
							: 1,
				},
			});
		}
	};
	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const handleCreateCommonSession = useCallback(
		async ({
			dist,
			tags,
			handleClose,
			itemIds,
			push,
			source,
			additionalSessionParams,
		}: {
			dist: Distribution;
			tags?: number[][];
			handleClose?: () => void;
			itemIds?: number[];
			push?: boolean;
			source: SessionSource;
			additionalSessionParams?: {
				title: string;
				icon: string;
				sessionSubType?: "DIAGNOSTIC";
				examId?: string;
				examStatus?: ExamStatus;
				sessionId?: number;
				collectionId?: number;
			};
		}) => {
			resetSessionStore();
			try {
				const numItemsRequested = Object.values(dist).reduce(
					(acc, curr) => acc + curr,
					0,
				);

				if (IS_MOCKED_SESSION) {
					const { mockedSessionData } = await import(
						"@features/session/constants/mocked-session-data"
					);
					const { data } = mockedSessionData;
					const mockItems = flattenSessionItems(
						data.sessionItems as unknown as BaseItemSchema[],
						true,
					);
					const newSessionResponse: SessionResponse = {
						id: data.id,
						numericId: data.numericId,
						collectionId: data.collectionId,
						startTime: data.startTime,
						sessionItems: mockItems,
					};

					handlePostSessionCreation({
						newSessionResponse,
						numItemsRequested: data.sessionItems.length,
						dist,
						tags,
						handleClose,
						push,
						source,
					});
					return true;
				}

				const resumeSessionId =
					additionalSessionParams?.examStatus === "ACTIVE"
						? additionalSessionParams?.sessionId
						: undefined;
				const isResumingSession = Boolean(resumeSessionId);

				let result: BackendSessionResponse;
				if (resumeSessionId) {
					result = await resumeSession({
						sessionId: resumeSessionId,
					});
				} else {
					const params = {
						collectionId:
							additionalSessionParams?.collectionId ||
							activeStudyPackCollectionId ||
							studyPackCollectionId!,
						projectId,
						organizationId,
						sessionType: additionalSessionParams ? "SUMMATIVE" : "FORMATIVE",
						sessionSubType: additionalSessionParams?.sessionSubType,
						tagIds: tags,
						itemIds,
						itemsSelected: numItemsRequested,
						itemsSelectedDistribution: dist,
						title: additionalSessionParams?.title,
						icon: additionalSessionParams?.icon,
						examId: additionalSessionParams?.examId,
					} as const;

					result = await createNewSession(params);
				}

				if (!result.sessionItems?.length) {
					showToast({
						message: "Failed to create session, try again",
					});
					const error = new Error(
						"Error creating formative session : session list is empty",
					);
					captureErrorToSentry(error, {
						error: serializeErrorForSentry(result),
					});
					return false;
				}

				const flattenedSessionItems = flattenSessionItems(
					result.sessionItems,
					result.sessionType !== "FORMATIVE",
				);

				// const newSessionResponse = finalMockedSessionResponse;

				const newSessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};

				handlePostSessionCreation({
					newSessionResponse,
					numItemsRequested,
					dist,
					tags,
					handleClose,
					push,
					isDiagnostic:
						additionalSessionParams?.sessionSubType === "DIAGNOSTIC",
					isSummativeTest: !!additionalSessionParams?.collectionId,
					isResumedSession: isResumingSession,
					source,
				});
				if (isResumingSession && userId) {
					reconcileSummativeAssessments({
						userId,
						projectId,
						organizationId,
					}).catch(() => undefined);
				}
				return true;
			} catch (_error) {
				toggleShowErrorDialog();
				const error = new Error("Error creating formative session");
				captureErrorToSentry(error, {
					error: serializeErrorForSentry(_error),
				});
				return false;
			}
		},
		[resetSessionStore, router, updateSession, createNewSession],
	);

	return {
		handleCreateCommonSession,
		showErrorDialog,
		toggleShowErrorDialog,
		createNewSession,
		endCurrentSession,
		resumeSession,
		isActiveStudyPackCollectionLoading,
		isCreatingSession:
			createSessionMutation.isPending || resumeSessionMutation.isPending,
		isEndingSession:
			endSessionMutation.isPending || resumeSessionMutation.isPending,
		createSessionError:
			createSessionMutation.error || resumeSessionMutation.error,
		endSessionError: endSessionMutation.error,
	};
};
